// c61_663 — per-user UI preferences (drawer pin / rail / hub order) mirrored on the server so
// they follow the operator across browsers, devices and deployments. localStorage stays the
// instant cache; the server copy is pulled once per sign-in and pushed (debounced) on change.
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
export const UI_PREFS_EVENT = "ss-ui-prefs";

const pending = {};
let timer = null;

export const orderKeyFor = (username) => `snagger_tab_order_${username || "anon"}`;

// c61_797 — per-account start page (a hub path, optionally with ?tab=…). Applied once per app open.
export const landingKeyFor = (username) => `ss_landing_${username || "anon"}`;
export const LANDING_EVENT = "ss-landing-page";
export const getLandingPage = (username) => localStorage.getItem(landingKeyFor(username)) || "/";
export const setLandingPage = (username, path) => {
  localStorage.setItem(landingKeyFor(username), path || "/");
  saveUiPref({ landing_page: path || "/" });
  window.dispatchEvent(new CustomEvent(LANDING_EVENT, { detail: { landing_page: path || "/" } }));
};

// c61_727 — ONE navigation style for every hub: rail (icon sidebar, expands on hover — the default),
// sidebar (full labels) or drawer (hamburger). Wide screens only; narrow screens always use the drawer.
export const NAV_STYLES = ["rail", "sidebar", "drawer"];
export const NAV_STYLE_KEY = "ss_nav_style";
export const getNavStyle = () => {
  const v = localStorage.getItem(NAV_STYLE_KEY);
  return NAV_STYLES.includes(v) ? v : "rail";
};
export const setNavStyle = (v) => {
  if (!NAV_STYLES.includes(v)) return;
  localStorage.setItem(NAV_STYLE_KEY, v);
  saveUiPref({ nav_style: v });
  window.dispatchEvent(new CustomEvent(UI_PREFS_EVENT, { detail: { nav_style: v } }));
};

export const applyUiPrefsToLocal = (prefs, username) => {
  if (!prefs) return;
  Object.entries(prefs.nav_pinned || {}).forEach(([hub, on]) => localStorage.setItem(`ss_nav_pinned_${hub}`, on ? "1" : "0"));
  Object.entries(prefs.nav_rail || {}).forEach(([hub, on]) => localStorage.setItem(`ss_nav_rail_${hub}`, on ? "1" : "0"));
  if (NAV_STYLES.includes(prefs.nav_style)) localStorage.setItem(NAV_STYLE_KEY, prefs.nav_style);
  if (Array.isArray(prefs.hub_order) && prefs.hub_order.length) localStorage.setItem(orderKeyFor(username), JSON.stringify(prefs.hub_order));
  // c61_797 — per-account start page
  if (typeof prefs.landing_page === "string") localStorage.setItem(landingKeyFor(username), prefs.landing_page);
  // c61_666 — workspace tabs follow the user to a new browser (lazy import: workspaceTabs imports this module)
  if (Array.isArray(prefs.open_tabs) && prefs.open_tabs.length) {
    import("./workspaceTabs").then((m) => m.adoptServerTabs(prefs.open_tabs)).catch(() => {});
  }
  // c61_738 — and the recently-closed pages
  if (Array.isArray(prefs.recent_tabs) && prefs.recent_tabs.length) {
    import("./workspaceTabs").then((m) => m.adoptServerRecent(prefs.recent_tabs)).catch(() => {});
  }
  window.dispatchEvent(new CustomEvent(UI_PREFS_EVENT, { detail: prefs }));
};

export const pullUiPrefs = async (username) => {
  try {
    const { data } = await axios.get(`${API}/api/auth/ui-prefs`);
    applyUiPrefsToLocal(data.ui_prefs, username);
  } catch { /* offline / old backend — local cache still works */ }
};

const flush = async () => {
  const body = { ...pending };
  Object.keys(pending).forEach((k) => delete pending[k]);
  timer = null;
  if (!Object.keys(body).length) return;
  try { await axios.put(`${API}/api/auth/ui-prefs`, body); } catch { /* best-effort */ }
};

export const saveUiPref = (patch) => {
  Object.entries(patch || {}).forEach(([k, v]) => {
    pending[k] = (Array.isArray(v) || v === null || typeof v !== "object") ? v : { ...(pending[k] || {}), ...v };
  });
  if (timer) clearTimeout(timer);
  timer = setTimeout(flush, 600);
};
